import { useEffect, useState } from "react";
import Button from "../../components/Button";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import { apiRequest } from "../../api/client";
import { useToast } from "../../context/ToastContext";

const emptySettings = {
  website_name: "",
  logo_url: "",
  contact_email: "",
  phone: "",
  hero_title: "",
  hero_subtitle: "",
  hero_button_text: "",
  hero_image_url: "",
  cta_text: "",
  social_links: {},
  about_section: {},
  benefits: [],
  content: {},
};

const defaultPromoStrip = ["Limited offer: 20% this week", "Use LAUNCH20", "Digital access"];
const minAdminPasswordLength = 8;
const emptyPasswordForm = {
  currentPassword: "",
  newPassword: "",
  confirmNewPassword: "",
};

function getPromoStrip(content) {
  const configured = Array.isArray(content?.promo_strip) ? content.promo_strip : [];
  return [...defaultPromoStrip].map((fallback, index) => String(configured?.[index] ?? fallback));
}

export default function AdminSettings() {
  const [settings, setSettings] = useState(emptySettings);
  const [promoStrip, setPromoStrip] = useState(defaultPromoStrip);
  const [jsonFields, setJsonFields] = useState({ social_links: "{}", about_section: "{}", benefits: "[]", content: "{}" });
  const [passwordForm, setPasswordForm] = useState(emptyPasswordForm);
  const [passwordStatus, setPasswordStatus] = useState({ type: "", message: "" });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const toast = useToast();

  useEffect(() => {
    apiRequest("/api/settings", { tokenType: "admin" })
      .then((response) => {
        const data = { ...emptySettings, ...response.data };
        setSettings(data);
        setJsonFields({
          social_links: JSON.stringify(data.social_links || {}, null, 2),
          about_section: JSON.stringify(data.about_section || {}, null, 2),
          benefits: JSON.stringify(data.benefits || [], null, 2),
          content: JSON.stringify(data.content || {}, null, 2),
        });
        setPromoStrip(getPromoStrip(data.content));
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  function update(field, value) {
    setSettings((current) => ({ ...current, [field]: value }));
  }

  function updatePromoStrip(index, value) {
    setPromoStrip((current) => current.map((item, itemIndex) => (itemIndex === index ? value : item)));
  }

  function updatePasswordForm(field, value) {
    setPasswordForm((current) => ({ ...current, [field]: value }));
    setPasswordStatus({ type: "", message: "" });
  }

  function validatePasswordForm() {
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmNewPassword) {
      return "All password fields are required.";
    }
    if (passwordForm.newPassword.length < minAdminPasswordLength) {
      return `New password must be at least ${minAdminPasswordLength} characters.`;
    }
    if (passwordForm.newPassword !== passwordForm.confirmNewPassword) {
      return "New password and confirmation do not match.";
    }
    return "";
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError("");
    try {
      const payload = {
        ...settings,
        social_links: JSON.parse(jsonFields.social_links || "{}"),
        about_section: JSON.parse(jsonFields.about_section || "{}"),
        benefits: JSON.parse(jsonFields.benefits || "[]"),
        content: {
          ...JSON.parse(jsonFields.content || "{}"),
          promo_strip: promoStrip.map((item) => item.trim()),
        },
      };
      const response = await apiRequest("/api/settings", { method: "PUT", body: payload, tokenType: "admin" });
      setSettings(response.data);
      setPromoStrip(getPromoStrip(response.data?.content));
      setJsonFields((current) => ({
        ...current,
        content: JSON.stringify(response.data?.content || {}, null, 2),
      }));
      toast?.show("Settings saved.");
    } catch (err) {
      setError(err.message);
    }
  }

  async function handlePasswordSubmit(event) {
    event.preventDefault();
    const validationError = validatePasswordForm();
    if (validationError) {
      setPasswordStatus({ type: "error", message: validationError });
      return;
    }

    setPasswordSaving(true);
    setPasswordStatus({ type: "", message: "" });
    try {
      const response = await apiRequest("/api/auth/admin/password", {
        method: "PUT",
        body: passwordForm,
        tokenType: "admin",
      });
      setPasswordForm(emptyPasswordForm);
      setPasswordStatus({ type: "success", message: response.message || "Admin password changed successfully." });
      toast?.show("Admin password changed.");
    } catch (err) {
      setPasswordStatus({ type: "error", message: err.message || "Could not change admin password." });
    } finally {
      setPasswordSaving(false);
    }
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div className="admin-page">
      <div className="admin-page-head"><div><span className="eyebrow">Website</span><h1>Settings</h1></div></div>
      <form className="admin-panel form-card" onSubmit={handleSubmit}>
        <div className="form-grid">
          <label><span>Website name</span><input value={settings.website_name || ""} onChange={(event) => update("website_name", event.target.value)} /></label>
          <label><span>Contact email</span><input value={settings.contact_email || ""} onChange={(event) => update("contact_email", event.target.value)} /></label>
        </div>
        <div className="form-grid">
          <label><span>Phone</span><input value={settings.phone || ""} onChange={(event) => update("phone", event.target.value)} /></label>
          <label><span>Hero button text</span><input value={settings.hero_button_text || ""} onChange={(event) => update("hero_button_text", event.target.value)} /></label>
        </div>
        <div className="form-grid">
          {promoStrip.map((item, index) => (
            <label key={index}>
              <span>Promo strip {index + 1}</span>
              <input value={item} onChange={(event) => updatePromoStrip(index, event.target.value)} />
            </label>
          ))}
        </div>
        <label><span>Hero title</span><input value={settings.hero_title || ""} onChange={(event) => update("hero_title", event.target.value)} /></label>
        <label><span>Hero subtitle</span><textarea rows="3" value={settings.hero_subtitle || ""} onChange={(event) => update("hero_subtitle", event.target.value)} /></label>
        <label><span>Social links JSON</span><textarea rows="6" value={jsonFields.social_links} onChange={(event) => setJsonFields({ ...jsonFields, social_links: event.target.value })} /></label>
        <label><span>About section JSON</span><textarea rows="6" value={jsonFields.about_section} onChange={(event) => setJsonFields({ ...jsonFields, about_section: event.target.value })} /></label>
        <label><span>Benefits JSON</span><textarea rows="6" value={jsonFields.benefits} onChange={(event) => setJsonFields({ ...jsonFields, benefits: event.target.value })} /></label>
        <label><span>Homepage content JSON</span><textarea rows="8" value={jsonFields.content} onChange={(event) => setJsonFields({ ...jsonFields, content: event.target.value })} /></label>
        {error && <p className="form-status error">{error}</p>}
        <Button type="submit">Save Settings</Button>
      </form>
      <form className="admin-panel form-card" onSubmit={handlePasswordSubmit}>
        <div className="form-heading">
          <div>
            <span>Security</span>
            <h2>Change Admin Password</h2>
          </div>
        </div>
        <div className="form-grid">
          <label>
            <span>Current password</span>
            <input
              type="password"
              value={passwordForm.currentPassword}
              autoComplete="current-password"
              onChange={(event) => updatePasswordForm("currentPassword", event.target.value)}
            />
          </label>
          <label>
            <span>New password</span>
            <input
              type="password"
              value={passwordForm.newPassword}
              minLength={minAdminPasswordLength}
              autoComplete="new-password"
              onChange={(event) => updatePasswordForm("newPassword", event.target.value)}
            />
          </label>
        </div>
        <label>
          <span>Confirm new password</span>
          <input
            type="password"
            value={passwordForm.confirmNewPassword}
            minLength={minAdminPasswordLength}
            autoComplete="new-password"
            onChange={(event) => updatePasswordForm("confirmNewPassword", event.target.value)}
          />
        </label>
        {passwordStatus.message && <p className={`form-status ${passwordStatus.type}`}>{passwordStatus.message}</p>}
        <Button type="submit" disabled={passwordSaving}>
          {passwordSaving ? "Changing..." : "Change Password"}
        </Button>
      </form>
    </div>
  );
}
